import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';

const CATEGORIES = [
  'Bible Stories',
  'Relationships',
  'Life Questions',
  'Identity',
  'All about God',
  'Life Principles'
];

const EditChildrenResource = () => {
  const { trackId, resourceId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [trackName, setTrackName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const [resource, setResource] = useState({
    category: '',
    title: '',
    subtitle: '',
    description: '',
    content: '',
    authorName: '',
    authorWebsite: '',
    imageURL: '',
    videoURL: '',
    videoTranscription: '',
    displayOrder: '',
    url: '' // Store the URL to extract article ID
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');

  // Load resource data on mount
  useEffect(() => {
    const fetchResource = async () => {
      try {
        const resourceDoc = await getDoc(
          doc(db, 'contentTracks', trackId, 'resources', resourceId)
        );
        
        if (!resourceDoc.exists()) {
          throw new Error('Resource not found');
        }

        // Also check if this is a children's track
        let isChildrenTrack = false;
        try {
          const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
          if (trackDoc.exists()) {
            isChildrenTrack = trackDoc.data().trackType === 'children';
          }
        } catch (trackError) {
          console.warn('Could not check track type:', trackError);
        }

        const data = resourceDoc.data();
        
        // For old resources, try to infer missing fields
        // Check if it has old format fields (text instead of content, format field, etc.)
        const isOldFormat = (data.text && !data.content) || data.format || (!data.category && isChildrenTrack);
        
        // Load article document to get any existing fields
        let articleData = {};
        if (data.url) {
          const articleUrlPattern = /\/article\/([^/?]+)/;
          const match = data.url.match(articleUrlPattern);
          if (match) {
            try {
              const articleDoc = await getDoc(doc(db, 'articles', match[1]));
              if (articleDoc.exists()) {
                articleData = articleDoc.data();
                console.log('Loaded existing article data:', articleData);
              }
            } catch (articleError) {
              console.warn('Could not load article document:', articleError);
            }
          }
        }

        // Merge resource data with article data, prioritizing resource data
        // Use content from article if resource doesn't have it (old format)
        const mergedContent = data.content || articleData.content || data.text || data.articleContent || '';
        const mergedImageURL = data.imageURL || articleData.imageURL || articleData.image || '';
        const mergedSubtitle = data.subtitle || articleData.subtitle || '';
        const mergedCategory = data.category || articleData.category || (isChildrenTrack ? 'Bible Stories' : '');
        const mergedAuthorName = data.authorName || articleData.authorName || articleData.author || '';
        const mergedAuthorWebsite = data.authorWebsite || articleData.authorWebsite || articleData.website || '';
        const mergedDescription = data.description || articleData.description || '';

        setResource({
          category: mergedCategory,
          title: data.title || '',
          subtitle: mergedSubtitle,
          description: mergedDescription,
          content: mergedContent,
          authorName: mergedAuthorName,
          authorWebsite: mergedAuthorWebsite,
          imageURL: mergedImageURL,
          videoURL: data.videoURL || '',
          videoTranscription: data.videoTranscription || '',
          displayOrder: data.displayOrder?.toString() || '',
          url: data.url || ''
        });

        // Set previews for existing media
        if (mergedImageURL) {
          setImagePreview(mergedImageURL);
        }
        if (data.videoURL) {
          setVideoPreview(data.videoURL);
        }

        console.log('Loaded resource (old format detected:', isOldFormat, '):', {
          resource: data,
          article: articleData,
          merged: {
            category: mergedCategory,
            hasContent: !!mergedContent,
            hasImage: !!mergedImageURL,
            hasSubtitle: !!mergedSubtitle
          }
        });
      } catch (error) {
        console.error('Error fetching resource:', error);
        alert('Failed to load resource');
        navigate(`/content-tracks/${trackId}`);
      } finally {
        setLoading(false);
      }
    };

    if (trackId && resourceId) {
      fetchResource();
    }
  }, [trackId, resourceId, navigate]);

  // Load track name
  useEffect(() => {
    const loadTrackName = async () => {
      if (!trackId) return;
      try {
        const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
        if (trackDoc.exists()) {
          const trackData = trackDoc.data();
          setTrackName(trackData.name || 'Content Track');
        }
      } catch (error) {
        console.error('Error loading track name:', error);
      }
    };

    loadTrackName();
  }, [trackId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setResource(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setSelectedImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    setSelectedVideo(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file) => {
    if (!file || !currentUser) return null;

    try {
      setUploadingMedia(true);
      const filename = `children-resources/${trackId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filename);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      return null;
    } finally {
      setUploadingMedia(false);
    }
  };

  const uploadVideo = async (file) => {
    if (!file || !currentUser) return null;

    try {
      setUploadingMedia(true);
      const filename = `children-resources/${trackId}/videos/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filename);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Failed to upload video. Please try again.');
      return null;
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trackId || !resourceId) return;

    // Ensure category is set - if not provided and this is a children's track, default to 'Bible Stories'
    let categoryToUse = resource.category;
    if (!categoryToUse) {
      try {
        const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
        if (trackDoc.exists() && trackDoc.data().trackType === 'children') {
          categoryToUse = 'Bible Stories'; // Default category for children's resources
        }
      } catch (error) {
        console.warn('Could not check track type for category default:', error);
      }
    }

    // Validate required fields
    if (!categoryToUse) {
      alert('Please select a category');
      return;
    }

    if (!resource.title || !resource.title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!resource.description || !resource.description.trim()) {
      alert('Please enter a description');
      return;
    }

    if (!resource.content || !resource.content.trim()) {
      alert('Please enter article content');
      return;
    }

    setSaving(true);

    try {
      // Upload image if selected
      let imageURL = resource.imageURL;
      if (selectedImage) {
        const uploadedImageURL = await uploadImage(selectedImage);
        if (uploadedImageURL) {
          imageURL = uploadedImageURL;
        } else {
          setSaving(false);
          return;
        }
      }

      // Upload video if selected
      let videoURL = resource.videoURL;
      if (selectedVideo) {
        const uploadedVideoURL = await uploadVideo(selectedVideo);
        if (uploadedVideoURL) {
          videoURL = uploadedVideoURL;
        } else {
          setSaving(false);
          return;
        }
      }

      const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
      
      // ALWAYS ensure article document exists and has all new format fields
      // This transforms old resources to new format
      let finalArticleUrl = resource.url || '';
      const articleUrlPattern = /\/article\/([^/?]+)/;
      const match = resource.url?.match(articleUrlPattern);
      const existingArticleId = match ? match[1] : null;

      // Prepare article data with ALL new format fields
      const articleData = {
        title: resource.title.trim(),
        content: resource.content.trim(), // REQUIRED for new format
        description: resource.description.trim(),
        subtitle: resource.subtitle.trim() || '',
        imageURL: imageURL || '',
        image: imageURL || '', // Compatibility field
        videoURL: videoURL || '',
        videoTranscription: resource.videoTranscription.trim() || '',
        author: resource.authorName.trim() || '', // Compatibility field
        authorName: resource.authorName.trim() || '',
        authorWebsite: resource.authorWebsite.trim() || '',
        website: resource.authorWebsite.trim() || '', // Compatibility field
        category: categoryToUse, // REQUIRED for mobile layout detection
        trackId: trackId, // REQUIRED for track type detection
        updatedAt: serverTimestamp(),
        updatedBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || ''
        }
      };

      if (existingArticleId) {
        try {
          // Check if article exists
          const articleRef = doc(db, 'articles', existingArticleId);
          const articleSnap = await getDoc(articleRef);
          
          if (articleSnap.exists()) {
            // Update existing article - this transforms old format to new
            console.log('🔄 UPDATING article document:', {
              articleId: existingArticleId,
              category: categoryToUse,
              trackId: trackId,
              hasImage: !!imageURL,
              hasSubtitle: !!resource.subtitle
            });
            await updateDoc(articleRef, articleData);
            console.log('✅ Article document updated successfully');
            finalArticleUrl = resource.url; // Keep existing URL
          } else {
            // Article ID in URL but doesn't exist - create new one
            articleData.createdAt = serverTimestamp();
            articleData.createdBy = {
              userId: currentUser?.uid || '',
              name: userData?.name || ''
            };
            delete articleData.updatedAt;
            delete articleData.updatedBy;
            
            const newArticleRef = await addDoc(collection(db, 'articles'), articleData);
            finalArticleUrl = `${articleDomain}/article/${newArticleRef.id}`;
          }
        } catch (articleError) {
          console.error('Error updating/creating article:', articleError);
          // Create new article if update fails
          try {
            articleData.createdAt = serverTimestamp();
            articleData.createdBy = {
              userId: currentUser?.uid || '',
              name: userData?.name || ''
            };
            delete articleData.updatedAt;
            delete articleData.updatedBy;
            
            const newArticleRef = await addDoc(collection(db, 'articles'), articleData);
            finalArticleUrl = `${articleDomain}/article/${newArticleRef.id}`;
          } catch (createError) {
            console.error('Error creating fallback article:', createError);
            throw createError; // Re-throw if we can't create article
          }
        }
      } else {
        // No article URL exists - create new article document
        console.log('🆕 CREATING new article document:', {
          category: categoryToUse,
          trackId: trackId,
          hasImage: !!imageURL
        });
        articleData.createdAt = serverTimestamp();
        articleData.createdBy = {
          userId: currentUser?.uid || '',
          name: userData?.name || ''
        };
        delete articleData.updatedAt;
        delete articleData.updatedBy;
        
        const articleRef = await addDoc(collection(db, 'articles'), articleData);
        finalArticleUrl = `${articleDomain}/article/${articleRef.id}`;
        console.log('✅ New article created:', articleRef.id, finalArticleUrl);
      }

      // Update the resource document with ALL new format fields
      // This ensures old resources are transformed to new format
      const updateData = {
        category: categoryToUse, // REQUIRED for children's detection
        title: resource.title.trim(),
        subtitle: resource.subtitle.trim() || '', // NEW field for image subtitle
        description: resource.description.trim(),
        content: resource.content.trim(), // REQUIRED - main article content
        authorName: resource.authorName.trim() || '',
        authorWebsite: resource.authorWebsite.trim() || '',
        imageURL: imageURL || '', // REQUIRED for image display
        videoURL: videoURL || '',
        videoTranscription: resource.videoTranscription.trim() || '',
        type: videoURL ? 'video' : 'article',
        url: finalArticleUrl, // ALWAYS ensure URL points to article document
        lastModified: serverTimestamp(),
        lastModifiedBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || ''
        }
      };

      // Only add displayOrder if it's a valid number - never set to undefined
      if (resource.displayOrder && typeof resource.displayOrder === 'string' && resource.displayOrder.trim()) {
        const displayOrder = parseInt(resource.displayOrder.trim(), 10);
        if (!Number.isNaN(displayOrder) && displayOrder > 0) {
          updateData.displayOrder = displayOrder;
        }
      }

      // Remove any undefined values to prevent Firestore errors
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(
        doc(db, 'contentTracks', trackId, 'resources', resourceId),
        updateData
      );

      navigate(`/content-tracks/${trackId}`);
    } catch (error) {
      console.error('Error updating children resource:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Failed to update resource: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto p-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-2">Edit Children&apos;s Resource</h1>
        <p className="text-sm text-gray-600 mb-6">
          Track: {trackName}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={resource.category}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select a category</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={resource.title}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required
              placeholder="Enter the title of the resource"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={resource.description}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              rows="3"
              required
              placeholder="Enter a brief description/summary of the resource"
            />
          </div>

          {/* Article Content */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Article Content <span className="text-red-500">*</span>
            </label>
            <textarea
              name="content"
              value={resource.content}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              rows="12"
              required
              placeholder="Enter the full article content, story text, or lesson material here..."
            />
            <p className="text-sm text-gray-500 mt-1">
              This is the main content that will be displayed to children. Include the full story, lesson, or article text here.
            </p>
          </div>

          {/* Author Name */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Author Name</label>
            <input
              type="text"
              name="authorName"
              value={resource.authorName}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="Enter author name"
            />
          </div>

          {/* Author Website */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Author Website</label>
            <input
              type="url"
              name="authorWebsite"
              value={resource.authorWebsite}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="https://example.com"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full p-2 border rounded"
            />
            {imagePreview && (
              <div className="mt-2">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-xs h-auto rounded border"
                />
              </div>
            )}
            {resource.imageURL && !selectedImage && (
              <div className="mt-2 text-sm text-gray-600">
                Current image URL: {resource.imageURL}
              </div>
            )}
            <input
              type="url"
              name="imageURL"
              value={resource.imageURL}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-2"
              placeholder="Or enter image URL"
            />
          </div>

          {/* Image Subtitle */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Image Subtitle</label>
            <input
              type="text"
              name="subtitle"
              value={resource.subtitle}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="Text that appears beneath the image on the webpage"
            />
            <p className="text-sm text-gray-500 mt-1">
              This text will appear directly below the image when displayed on tmrsrc.com
            </p>
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Video</label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="w-full p-2 border rounded"
            />
            {videoPreview && (
              <div className="mt-2">
                <video
                  src={videoPreview}
                  controls
                  className="max-w-xs rounded border"
                />
              </div>
            )}
            {resource.videoURL && !selectedVideo && (
              <div className="mt-2 text-sm text-gray-600">
                Current video URL: {resource.videoURL}
              </div>
            )}
            <input
              type="url"
              name="videoURL"
              value={resource.videoURL}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-2"
              placeholder="Or enter video URL"
            />
          </div>

          {/* Video Transcription */}
          {resource.videoURL && (
            <div>
              <label className="block text-gray-700 font-bold mb-2">Video Transcription</label>
              <textarea
                name="videoTranscription"
                value={resource.videoTranscription}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                rows="8"
                placeholder="Enter the full transcription of the video"
              />
            </div>
          )}

          {/* Display Order */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Display Order (optional)</label>
            <input
              type="number"
              name="displayOrder"
              value={resource.displayOrder}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              min="1"
              placeholder="Lower numbers appear first"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(`/content-tracks/${trackId}`)}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-[#0076BE] text-white px-6 py-2 rounded hover:bg-[#005c94] disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={saving || uploadingMedia}
            >
              {saving || uploadingMedia
                ? (uploadingMedia ? 'Uploading...' : 'Saving...')
                : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditChildrenResource;
